import { SelectionType } from '@superbet-group/web.types';
import { differenceBy as _differenceBy } from 'lodash';

import { logger } from '@app/logger';
import OfferEvent from '@core/models/offer/OfferEvent';
import ISelection from '@models/betSlip/ISelection';
import SelectionChange from '@models/betSlip/SelectionChange';
import SportSelection from '@models/betSlip/SportSelection';
import { MUTATION_TYPE } from '@store/modules/ui/betSlip/mutations';
import {
    MatchSubscription,
    BetSlipContext,
} from '@store/modules/ui/betSlip/types';
import { findSelectionsByEventId } from '@store/modules/ui/shared/betSlip/Helpers';
import {
    OutOfSyncUpdate,
    SseUpdate,
} from '@core/services/offer/_private/sse/types';

interface MatchSubscriptions {
    add(context: BetSlipContext, eventId: ReturnType<ISelection['getEventId']>): void;
    addAll(context: BetSlipContext, eventIds: ReturnType<ISelection['getEventId']>[]): void;
    remove(eventId: ReturnType<ISelection['getEventId']>): void;
    updateAll(
        context: BetSlipContext,
        previousSelections: ISelection[],
        nextSelections: ISelection[]
    ): void;
    clear(): void;
}

function getSportEventIds(selections: ISelection[]): number[] {
    return selections.filter((s) => s.isSportType()).map((s) => s.getEventId());
}

export function createMatchSubscriptions(
    subscribeToEvent: (eventId: OfferEvent['id'], callback: (match: SseUpdate<OfferEvent>) => void) => MatchSubscription,
    getEvents: (eventIds: OfferEvent['id'][]) => Promise<OfferEvent[]>,
): MatchSubscriptions {
    const subscriptions = new Map<OfferEvent['id'], MatchSubscription>();

    function addMatchSubscription(
        context: BetSlipContext,
        eventId: ReturnType<ISelection['getEventId']>,
    ): void {
        if (subscriptions.has(eventId)) {
            const msg = `Subscription to ${eventId} already set`;
            logger.warn(msg);
            return;
        }

        const subscription = subscribeToEvent(eventId, (event) => {
            if (event === OutOfSyncUpdate) {
                getEvents([eventId]);
            } else {
                applyEventUpdateForBetSlip(context, event);
            }
        });
        subscriptions.set(eventId, subscription);
    }

    function addMultipleMatchSubscriptions(
        context: BetSlipContext,
        eventIds: ReturnType<ISelection['getEventId']>[],
    ): void {
        eventIds.forEach((eventId) => addMatchSubscription(context, eventId));
    }

    function removeMatchSubscription(eventId: ReturnType<ISelection['getEventId']>): void {
        const subscription = subscriptions.get(eventId);
        subscription?.unsubscribe();
        subscriptions.delete(eventId);
    }

    function clearMatchSubscriptions(): void {
        const subscribedEventIds = Array.from(subscriptions.keys());
        subscribedEventIds.forEach((eventId) => removeMatchSubscription(eventId));
    }

    function updateSubscriptions(
        context: BetSlipContext,
        previousSelections: ISelection[],
        nextSelections: ISelection[],
    ): void {
        const previousEventIds = getSportEventIds(previousSelections);
        const nextEventIds = getSportEventIds(nextSelections);

        const eventsToUnsubscribeFrom = _differenceBy(previousEventIds, nextEventIds);
        const eventsToSubscribeTo = _differenceBy(nextEventIds, previousEventIds);

        eventsToUnsubscribeFrom.map((eventId) => removeMatchSubscription(eventId));
        eventsToSubscribeTo.map((eventId) => addMatchSubscription(context, eventId));
    }

    function applyEventUpdateForBetSlip(context: BetSlipContext, event: OfferEvent): void {
        const selections = context.getters.selections;
        if (selections.length === 0) {
            return;
        }
        const selectionsFromEvent = findSelectionsByEventId(selections, event.id, SelectionType.sport);
        if (selectionsFromEvent.length === 0) {
            return;
        }
        selectionsFromEvent.forEach((selection) => {
            const newSelection = createNewSelection(event, selection as SportSelection);

            processSelectionChange(
                context,
                new SelectionChange({
                    newSelection,
                    selection,
                }),
            );
        });
    }

    function createNewSelection(event: OfferEvent, selection: SportSelection): SportSelection {
        // Get the new odd from updated event based on old odd info
        const odd = event.getOdd({
            id: selection.getOddId()!,
            specialBetValue: selection.getOddSpecialBetValue(),
            uuid: selection.getOddUuid() ?? undefined,
        }) ?? null;

        const newSelection = SportSelection.createItem(event, odd);
        newSelection.isFixed = selection.isFixed;

        return newSelection;
    }

    async function processSelectionChange(
        context: BetSlipContext,
        change: SelectionChange,
    ): Promise<void> {
        const { commit, dispatch, getters } = context;

        const isEventFinished = change.newSelection?.event?.isFinished();
        const isOddRemoval = change.newSelection?.odd == null;

        if (isEventFinished) {
            await dispatchSelectionUpdate(context, change);
        } else if (isOddRemoval) {
            await dispatchOddRemoval(context, change);
        } else if (getters.autoApplyChanges) {
            await dispatchSelectionUpdate(context, change);
        } else {
            if (change.selection.getOddValue() !== change.newSelection?.getOddValue()) {
                commit(MUTATION_TYPE.ADD_SELECTION_CHANGE, change);
            }
        }
        await dispatch('calculatePayout');
        await dispatch('checkBetSlipValidation');
    }

    function dispatchSelectionUpdate(
        context: BetSlipContext,
        change: SelectionChange,
    ): Promise<void> {
        return context.dispatch(
            'updateSelection',
            { selection: change.newSelection! },
        );
    }

    function dispatchOddRemoval(
        context: BetSlipContext,
        change: SelectionChange,
    ): Promise<void> {
        return context.dispatch(
            'markSelectionAsFinished',
            { selection: change.selection },
        );
    }

    return {
        add: addMatchSubscription,
        addAll: addMultipleMatchSubscriptions,
        remove: removeMatchSubscription,
        updateAll: updateSubscriptions,
        clear: clearMatchSubscriptions,
    };
}
